import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Truck,
  Building,
  Wrench,
  ArrowLeft,
  ArrowRight,
  Check,
  IndianRupee,
  MapPin,
  FileText,
  Image,
  Info,
} from "lucide-react";
import api from "@/lib/api";
import { AuthContext } from "@/App";
import { toast } from "sonner";

const CATEGORIES = {
  equipment: {
    name: "Equipment",
    icon: Wrench,
    color: "bg-orange-500",
    description: "Hywa, JCB, Cranes, Forklifts, and more",
    subcategories: ["Hywa", "JCB", "Crane", "Forklift", "Excavator", "Bulldozer", "Concrete Mixer", "Generator", "Other Equipment"]
  },
  spaces: {
    name: "Spaces",
    icon: Building,
    color: "bg-blue-500",
    description: "Commercial, Warehouse, Co-working spaces",
    subcategories: ["Commercial Shop", "Warehouse", "Godown", "Co-working Space", "Office Space", "Industrial Shed", "Cold Storage", "Other Space"]
  },
  transport: {
    name: "Transport",
    icon: Truck,
    color: "bg-green-500",
    description: "Trucks, Tempo, Fleet services",
    subcategories: ["Truck", "Tempo", "Container", "Tanker", "Trailer", "Mini Truck", "Fleet Services", "Logistics", "Other Transport"]
  }
};

const STEPS = [
  { id: 1, title: "Category", icon: Wrench },
  { id: 2, title: "Details", icon: FileText },
  { id: 3, title: "Pricing", icon: IndianRupee },
  { id: 4, title: "Review", icon: Check },
];

export default function ListRental() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    category: "",
    subcategory: "",
    title: "",
    description: "",
    location: "",
    specifications: "",
    availability: "Available",
    min_rental_period: "",
    images: [],
    brand: "",
    model: "",
    year: "",
    capacity: "",
    // Pricing
    hourly_rate: "",
    daily_rate: "",
    monthly_rate: "",
    is_negotiable: false,
  });

  const updateForm = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.category && formData.subcategory;
      case 2:
        return formData.title && formData.description && formData.location;
      case 3:
        return formData.is_negotiable || formData.hourly_rate || formData.daily_rate || formData.monthly_rate;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (canProceed()) {
      setCurrentStep(currentStep + 1);
    } else {
      toast.error("Please fill in all required fields");
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
        daily_rate: formData.daily_rate ? parseFloat(formData.daily_rate) : null,
        monthly_rate: formData.monthly_rate ? parseFloat(formData.monthly_rate) : null,
        year: formData.year ? parseInt(formData.year) : null,
      };

      await api.post("/rentals", payload);
      toast.success("Rental listing created successfully!");
      navigate("/rentals");
    } catch (error) {
      console.error("Failed to create rental:", error);
      toast.error(error.response?.data?.detail || "Failed to create listing");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    navigate("/auth");
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/rentals")}
            className="flex items-center text-slate-600 hover:text-slate-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Rentals
          </button>
          <h1 className="text-3xl font-bold text-slate-900">List Your Rental</h1>
          <p className="text-slate-600 mt-1">Rent out your equipment, spaces, or transport services</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              return (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all ${
                    isActive ? "border-blue-600 bg-blue-600 text-white" :
                    isCompleted ? "border-emerald-500 bg-emerald-500 text-white" :
                    "border-slate-300 bg-white text-slate-400"
                  }`}>
                    {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <span className={`ml-2 text-sm font-medium hidden sm:block ${
                    isActive ? "text-blue-600" : isCompleted ? "text-emerald-600" : "text-slate-400"
                  }`}>
                    {step.title}
                  </span>
                  {index < STEPS.length - 1 && (
                    <div className={`w-12 sm:w-24 h-1 mx-2 sm:mx-4 rounded ${
                      isCompleted ? "bg-emerald-500" : "bg-slate-200"
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <Card data-testid="list-rental-form">
          <CardContent className="pt-6">
            {/* Step 1: Category Selection */}
            {currentStep === 1 && (
              <div className="space-y-6" data-testid="step-1-category">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-2">Select Category</h2>
                  <p className="text-slate-600 mb-6">What type of rental are you listing?</p>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  {Object.entries(CATEGORIES).map(([key, cat]) => {
                    const Icon = cat.icon;
                    const isSelected = formData.category === key;
                    return (
                      <button
                        key={key}
                        onClick={() => updateForm("category", key)}
                        className={`p-6 rounded-xl border-2 text-left transition-all ${
                          isSelected
                            ? `border-blue-500 bg-blue-50 ring-2 ring-blue-200`
                            : "border-slate-200 hover:border-slate-300 bg-white"
                        }`}
                        data-testid={`category-option-${key}`}
                      >
                        <div className={`w-12 h-12 rounded-lg ${cat.color} flex items-center justify-center mb-4`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="font-semibold text-slate-900">{cat.name}</h3>
                        <p className="text-sm text-slate-500 mt-1">{cat.description}</p>
                      </button>
                    );
                  })}
                </div>

                {formData.category && (
                  <div className="mt-6">
                    <Label>Subcategory *</Label>
                    <Select
                      value={formData.subcategory}
                      onValueChange={(val) => updateForm("subcategory", val)}
                    >
                      <SelectTrigger className="mt-2" data-testid="subcategory-select">
                        <SelectValue placeholder="Select subcategory" />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES[formData.category]?.subcategories.map((sub) => (
                          <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Details */}
            {currentStep === 2 && (
              <div className="space-y-6" data-testid="step-2-details">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-2">Rental Details</h2>
                  <p className="text-slate-600 mb-6">Provide information about your rental</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      placeholder="e.g., JCB 3DX Backhoe Loader for Rent"
                      value={formData.title}
                      onChange={(e) => updateForm("title", e.target.value)}
                      className="mt-2"
                      data-testid="title-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      rows={4}
                      placeholder="Describe your rental in detail - condition, features, usage terms, etc."
                      value={formData.description}
                      onChange={(e) => updateForm("description", e.target.value)}
                      className="mt-2"
                      data-testid="description-input"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="location">Location *</Label>
                      <div className="relative mt-2">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="location"
                          placeholder="City, Area"
                          value={formData.location}
                          onChange={(e) => updateForm("location", e.target.value)}
                          className="pl-10"
                          data-testid="location-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="availability">Availability</Label>
                      <Select
                        value={formData.availability}
                        onValueChange={(val) => updateForm("availability", val)}
                      >
                        <SelectTrigger className="mt-2" data-testid="availability-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Available">Available</SelectItem>
                          <SelectItem value="Limited">Limited</SelectItem>
                          <SelectItem value="On Demand">On Demand</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="specifications">Specifications</Label>
                    <Textarea
                      id="specifications"
                      rows={3}
                      placeholder="Technical specs, dimensions, weight capacity, etc."
                      value={formData.specifications}
                      onChange={(e) => updateForm("specifications", e.target.value)}
                      className="mt-2"
                      data-testid="specifications-input"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="brand">Brand</Label>
                      <Input
                        id="brand"
                        placeholder="e.g., JCB, Tata"
                        value={formData.brand}
                        onChange={(e) => updateForm("brand", e.target.value)}
                        className="mt-2"
                        data-testid="brand-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="model">Model</Label>
                      <Input
                        id="model"
                        placeholder="e.g., 3DX"
                        value={formData.model}
                        onChange={(e) => updateForm("model", e.target.value)}
                        className="mt-2"
                        data-testid="model-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="year">Year</Label>
                      <Input
                        id="year"
                        type="number"
                        placeholder="e.g., 2022"
                        value={formData.year}
                        onChange={(e) => updateForm("year", e.target.value)}
                        className="mt-2"
                        data-testid="year-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="capacity">Capacity</Label>
                      <Input
                        id="capacity"
                        placeholder="e.g., 10 tons"
                        value={formData.capacity}
                        onChange={(e) => updateForm("capacity", e.target.value)}
                        className="mt-2"
                        data-testid="capacity-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="min_rental_period">Minimum Rental Period</Label>
                    <Input
                      id="min_rental_period"
                      placeholder="e.g., 1 day, 1 week, 1 month"
                      value={formData.min_rental_period}
                      onChange={(e) => updateForm("min_rental_period", e.target.value)}
                      className="mt-2"
                      data-testid="min-rental-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Pricing */}
            {currentStep === 3 && (
              <div className="space-y-6" data-testid="step-3-pricing">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-2">Set Your Rates</h2>
                  <p className="text-slate-600 mb-6">Define your rental pricing (at least one rate or mark as negotiable)</p>
                </div>

                <div className="grid sm:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="hourly_rate">Hourly Rate</Label>
                    <div className="relative">
                      <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="hourly_rate"
                        type="number"
                        placeholder="0"
                        value={formData.hourly_rate}
                        onChange={(e) => updateForm("hourly_rate", e.target.value)}
                        className="pl-10"
                        data-testid="hourly-rate-input"
                      />
                    </div>
                    <p className="text-xs text-slate-500">Per hour</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="daily_rate">Daily Rate</Label>
                    <div className="relative">
                      <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="daily_rate"
                        type="number"
                        placeholder="0"
                        value={formData.daily_rate}
                        onChange={(e) => updateForm("daily_rate", e.target.value)}
                        className="pl-10"
                        data-testid="daily-rate-input"
                      />
                    </div>
                    <p className="text-xs text-slate-500">Per day</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="monthly_rate">Monthly Rate</Label>
                    <div className="relative">
                      <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="monthly_rate"
                        type="number"
                        placeholder="0"
                        value={formData.monthly_rate}
                        onChange={(e) => updateForm("monthly_rate", e.target.value)}
                        className="pl-10"
                        data-testid="monthly-rate-input"
                      />
                    </div>
                    <p className="text-xs text-slate-500">Per month</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-lg">
                  <Switch
                    id="is_negotiable"
                    checked={formData.is_negotiable}
                    onCheckedChange={(checked) => updateForm("is_negotiable", checked)}
                    data-testid="negotiable-switch"
                  />
                  <div>
                    <Label htmlFor="is_negotiable" className="text-base font-medium cursor-pointer">
                      Price is Negotiable
                    </Label>
                    <p className="text-sm text-slate-500">Allow potential renters to negotiate the price</p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">Pricing Tips</p>
                    <ul className="mt-1 space-y-1 text-blue-700">
                      <li>• Research market rates for similar rentals</li>
                      <li>• Offer discounts for longer rental periods</li>
                      <li>• Factor in maintenance and operating costs</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Review */}
            {currentStep === 4 && (
              <div className="space-y-6" data-testid="step-4-review">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-2">Review Your Listing</h2>
                  <p className="text-slate-600 mb-6">Make sure everything looks good before publishing</p>
                </div>

                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-slate-500">Category</p>
                      <p className="font-medium text-slate-900">{CATEGORIES[formData.category]?.name}</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-slate-500">Subcategory</p>
                      <p className="font-medium text-slate-900">{formData.subcategory}</p>
                    </div>
                  </div>

                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-sm text-slate-500">Title</p>
                    <p className="font-medium text-slate-900">{formData.title}</p>
                  </div>

                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-sm text-slate-500">Description</p>
                    <p className="text-slate-900">{formData.description}</p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-slate-500">Location</p>
                      <p className="font-medium text-slate-900">{formData.location}</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-slate-500">Availability</p>
                      <p className="font-medium text-slate-900">{formData.availability}</p>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                    <p className="text-sm text-blue-600 font-medium mb-2">Pricing</p>
                    <div className="flex flex-wrap gap-4">
                      {formData.hourly_rate && (
                        <span className="text-slate-900">₹{parseFloat(formData.hourly_rate).toLocaleString('en-IN')}/hr</span>
                      )}
                      {formData.daily_rate && (
                        <span className="text-slate-900">₹{parseFloat(formData.daily_rate).toLocaleString('en-IN')}/day</span>
                      )}
                      {formData.monthly_rate && (
                        <span className="text-slate-900">₹{parseFloat(formData.monthly_rate).toLocaleString('en-IN')}/mo</span>
                      )}
                      {formData.is_negotiable && (
                        <span className="text-green-600 font-medium">Negotiable</span>
                      )}
                      {!formData.hourly_rate && !formData.daily_rate && !formData.monthly_rate && formData.is_negotiable && (
                        <span className="text-blue-600">Contact for Price</span>
                      )}
                    </div>
                  </div>

                  {(formData.brand || formData.model || formData.year || formData.capacity) && (
                    <div className="grid sm:grid-cols-4 gap-4">
                      {formData.brand && (
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <p className="text-sm text-slate-500">Brand</p>
                          <p className="font-medium text-slate-900">{formData.brand}</p>
                        </div>
                      )}
                      {formData.model && (
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <p className="text-sm text-slate-500">Model</p>
                          <p className="font-medium text-slate-900">{formData.model}</p>
                        </div>
                      )}
                      {formData.year && (
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <p className="text-sm text-slate-500">Year</p>
                          <p className="font-medium text-slate-900">{formData.year}</p>
                        </div>
                      )}
                      {formData.capacity && (
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <p className="text-sm text-slate-500">Capacity</p>
                          <p className="font-medium text-slate-900">{formData.capacity}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              {currentStep > 1 ? (
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(currentStep - 1)}
                  data-testid="prev-step-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
              ) : (
                <div />
              )}

              {currentStep < 4 ? (
                <Button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  data-testid="next-step-btn"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="submit-listing-btn"
                >
                  {submitting ? "Creating..." : "Publish Listing"}
                  <Check className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
